// mds-dojo — dojox/widget/Standby — FAITHFUL port of references/dojox/widget/Standby.js.
//
// A Standby/Busy/Disable/Blocking overlay widget indicating a particular DOM node is
// processing and cannot be interacted with. Uses absolute positioning to lay a
// translucent underlay + a centered spinner image (or text) over a target node.
// The oracle app uses this (more/_WidgetBaseLite.StandbyStart → new Standby({target}),
// startup(), show()/hide()), so it is ported carefully.
//
// PORT NOTES (deviations from the AMD source, per the mds-dojo recipe):
//   - AMD define(...) → ESM imports; classic declare("dojox.widget.Standby",
//     [_Widget, _TemplatedMixin], {...}) kept via the faithful declare-runtime.
//   - dojo/_base/kernel dropped (only used for the unimplemented kernel.experimental()).
//   - has("ie")/has("opera")/has("quirks")/has("webkit") legacy branches removed
//     (IE7 zoom fix-node, IE/Opera RTL scroll compensation, IE/WebKit overflow
//     shims). The evergreen (non-IE) code paths are kept verbatim.
//   - mds-dojo has no dojo/_base/event, dojo/window, dojo/_base/fx or dojo/fx, and
//     dojo/dom-geometry omits getContentBox / scroll-inclusive position — the tiny
//     bits Standby needs from each are inlined below (event.stop, viewport getBox,
//     getContentBox + page-coordinate position, opacity animateProperty + combine).
//   - require.toUrl(loading.gif) default image → a self-contained inline SVG spinner
//     data-URI (no asset pipeline in the Vite twin).

import declare from '../../dojo/_base/declare-runtime';
import array from '../../dojo/_base/array';
import dom from '../../dojo/dom';
import attr from '../../dojo/dom-attr';
import construct from '../../dojo/dom-construct';
import _geometry from '../../dojo/dom-geometry';
import domStyle from '../../dojo/dom-style';
import baseWindow from '../../dojo/_base/window';
import _Widget from '../../dijit/_Widget';
import _TemplatedMixin from '../../dijit/_TemplatedMixin';
import registry from '../../dijit/registry';

// --- inlined substitutes for modules mds-dojo does not ship -----------------

// dojo/_base/event.stop — halt an event (preventDefault + stopPropagation).
var event: any = {
  stop: function (e: any) {
    if (e) {
      if (e.preventDefault) { e.preventDefault(); }
      if (e.stopPropagation) { e.stopPropagation(); }
    }
  }
};

// dojo/window — only getBox() is used (viewport size + scroll offsets).
var winUtils: any = {
  getBox: function () {
    var d = document.documentElement;
    return {
      w: d.clientWidth || window.innerWidth,
      h: d.clientHeight || window.innerHeight,
      l: window.pageXOffset || d.scrollLeft || 0,
      t: window.pageYOffset || d.scrollTop || 0
    };
  }
};

// dojo/dom-geometry — mds ships getMarginBox but not getContentBox, and its
// position() returns viewport (not page) coordinates. Standby uses position with
// includeScroll=true, so we add the scroll offset here.
var geometry: any = {
  getMarginBox: _geometry.getMarginBox,
  getContentBox: function (node: any) {
    return { l: node.clientLeft, t: node.clientTop, w: node.clientWidth, h: node.clientHeight };
  },
  position: function (node: any) {
    var r = node.getBoundingClientRect();
    return {
      x: r.left + (window.pageXOffset || document.documentElement.scrollLeft || 0),
      y: r.top + (window.pageYOffset || document.documentElement.scrollTop || 0),
      w: r.width,
      h: r.height
    };
  }
};

// dojo/_base/fx.animateProperty (opacity tween) + dojo/fx.combine — the minimal
// subset Standby's fade in/out needs. onEnd is invoked with `this` === the anim
// object (which exposes `.node`), matching dojo's contract.
function _animateProperty(args: any): any {
  var node = args.node;
  var duration = args.duration || 350;
  var props = args.properties || {};
  var op = props.opacity || { start: 0, end: 1 };
  var anim: any = {
    node: node,
    _raf: null,
    _running: false,
    play: function () {
      anim._running = true;
      var t0: number | null = null;
      var frame = function (ts: number) {
        if (!anim._running) { return; }
        if (t0 === null) { t0 = ts; }
        var f = duration <= 0 ? 1 : Math.min(1, (ts - t0) / duration);
        node.style.opacity = String(op.start + (op.end - op.start) * f);
        if (f < 1) {
          anim._raf = requestAnimationFrame(frame);
        } else {
          anim._raf = null;
          anim._running = false;
          if (args.onEnd) { args.onEnd.call(anim); }
        }
      };
      anim._raf = requestAnimationFrame(frame);
    },
    stop: function () {
      anim._running = false;
      if (anim._raf) { cancelAnimationFrame(anim._raf); anim._raf = null; }
    }
  };
  return anim;
}
var baseFx: any = { animateProperty: _animateProperty };
var fx: any = {
  combine: function (anims: any) {
    return {
      play: function () { array.forEach(anims, function (a: any) { a.play(); }); },
      stop: function () { array.forEach(anims, function (a: any) { a.stop(); }); }
    };
  }
};

// Self-contained default spinner (was require.toUrl("dojox/widget/Standby/images/loading.gif")).
var DEFAULT_IMAGE =
  "data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='32'%20height='32'%20viewBox='0%200%2032%2032'%3E" +
  "%3Ccircle%20cx='16'%20cy='16'%20r='12'%20fill='none'%20stroke='%23888'%20stroke-width='4'%20stroke-linecap='round'%20" +
  "stroke-dasharray='56'%20stroke-dashoffset='14'%3E%3CanimateTransform%20attributeName='transform'%20type='rotate'%20" +
  "from='0%2016%2016'%20to='360%2016%2016'%20dur='0.8s'%20repeatCount='indefinite'/%3E%3C/circle%3E%3C/svg%3E";

// ----------------------------------------------------------------------------

export default declare('dojox.widget.Standby', [_Widget, _TemplatedMixin], {
  // summary:
  //		A widget designed to act as a Standby/Busy/Disable/Blocking widget to
  //		indicate a particular DOM node is processing and cannot be clicked on at
  //		this time. Uses absolute positioning to apply the overlay and image.

  // image: String
  //		A URL to an image to center within the blocking overlay. Default = spinner.
  image: DEFAULT_IMAGE,

  // imageText: String
  //		Text to set on the ALT tag of the image.
  imageText: 'Please Wait...', // TODO: i18n

  // text: String
  //		Text/HTML to display in the center of the overlay (used if image is disabled).
  text: 'Please wait...',

  // centerIndicator: String
  //		'image' (use image + alt text) or 'text' (use a Text/HTML node). Default 'image'.
  centerIndicator: 'image',

  // target: DOMNode||DOMID(String)||WidgetID(String)
  //		The target to overlay when active.
  target: '',

  // color: String
  //		The color to set the overlay. Should be in #XXXXXX form. Default light gray.
  color: '#C0C0C0',

  // duration: Integer
  //		How long the show and hide effects take in milliseconds. Default 500.
  duration: 500,

  // zIndex: String
  //		'auto' (computed from parent zIndex) or a particular value.
  zIndex: 'auto',

  // opacity: float
  //		The opacity of the overlay when displayed/faded in. Default 0.75.
  opacity: 0.75,

  // templateString: [protected] String
  //		The template defining the basics of the widget. No external file needed.
  templateString:
    '<div>' +
      '<div style="display: none; opacity: 0; z-index: 9999; ' +
        'position: absolute; cursor:wait;" data-dojo-attach-point="_underlayNode"></div>' +
      '<img src="${image}" style="opacity: 0; display: none; z-index: -10000; ' +
        'position: absolute; top: 0px; left: 0px; cursor:wait;" ' +
        'data-dojo-attach-point="_imageNode">' +
      '<div style="opacity: 0; display: none; z-index: -10000; position: absolute; ' +
        'top: 0px;" data-dojo-attach-point="_textNode"></div>' +
    '</div>',

  // _underlayNode: [private] DOMNode — the translucent underlay.
  _underlayNode: null,
  // _imageNode: [private] DOMNode — the image node.
  _imageNode: null,
  // _textNode: [private] DOMNode — the div for text/HTML center content.
  _textNode: null,
  // _centerNode: [private] DOMNode — the active center node (image or text).
  _centerNode: null,
  // _displayed: [private] Boolean — whether the overlay is displayed.
  _displayed: false,
  // _resizeCheck: [private] Object — handle to the interval that watches the target.
  _resizeCheck: null,
  // _started: [private] Boolean — trap flag so startup only processes once.
  _started: false,
  // _parent: [private] DOMNode — wrapping div for the widget.
  _parent: null,

  startup: function (args) {
    // summary:
    //		Over-ride of the basic widget startup. Configures the target node and image.
    if (!this._started) {
      if (typeof this.target === 'string') {
        var w = registry.byId(this.target);
        this.target = w ? w.domNode : dom.byId(this.target);
      }

      if (this.text) {
        this._textNode.innerHTML = this.text;
      }
      if (this.centerIndicator === 'image') {
        this._centerNode = this._imageNode;
        attr.set(this._imageNode, 'src', this.image);
        attr.set(this._imageNode, 'alt', this.imageText);
      } else {
        this._centerNode = this._textNode;
      }
      domStyle.set(this._underlayNode, {
        display: 'none',
        backgroundColor: this.color
      });
      domStyle.set(this._centerNode, 'display', 'none');
      this.connect(this._underlayNode, 'onclick', '_ignore');

      // Last thing to do is move the widget's parent, if any, to the current document
      // body. Avoids parent relative/absolute positioning weirdness.
      if (this.domNode.parentNode && this.domNode.parentNode != baseWindow.body()) {
        baseWindow.body().appendChild(this.domNode);
      }

      this.inherited(arguments);
    }
  },

  show: function () {
    // summary:
    //		Display the blocking overlay and busy/status icon or text.
    if (!this._displayed) {
      if (this._anim) {
        this._anim.stop();
        delete this._anim;
      }
      this._displayed = true;
      this._size();
      this._disableOverflow();
      this._fadeIn();
    }
  },

  hide: function () {
    // summary:
    //		Hide the blocking overlay and status icon or text.
    if (this._displayed) {
      // Ideally would come up with something better than try/catch, but don't see
      // another simple workaround for dojo tickets #18196 and #14984.
      try {
        if (this._anim) {
          this._anim.stop();
          delete this._anim;
        }
        this._size();
      } catch (e) {
        console.error(e);
      } finally {
        this._fadeOut();
        this._displayed = false;
        if (this._resizeCheck !== null) {
          clearInterval(this._resizeCheck);
          this._resizeCheck = null;
        }
      }
    }
  },

  isVisible: function () {
    // summary:
    //		Helper so you can test if the widget is already visible or not.
    return this._displayed; // boolean
  },

  onShow: function () {
    // summary:
    //		Event that fires when the display of the Standby completes.
  },

  onHide: function () {
    // summary:
    //		Event that fires when the hide of the Standby completes.
  },

  uninitialize: function () {
    // summary:
    //		Over-ride to hide the widget, which clears intervals, before cleanup.
    this._displayed = false;
    if (this._resizeCheck) {
      clearInterval(this._resizeCheck);
    }
    domStyle.set(this._centerNode, 'display', 'none');
    domStyle.set(this._underlayNode, 'display', 'none');
    if (this._anim) {
      this._anim.stop();
      delete this._anim;
    }
    this.target = null;
    this._imageNode = null;
    this._textNode = null;
    this._centerNode = null;
    this.inherited(arguments);
  },

  _size: function () {
    // summary:
    //		Internal function that handles resizing the overlay and centering of the
    //		image on window resizing.
    // tags:
    //		private
    if (this._displayed) {
      var scrollers = this._scrollerWidths();

      var target = this.target;

      // Show the image and make sure the zIndex is set high.
      var curStyle = domStyle.get(this._centerNode, 'display');
      domStyle.set(this._centerNode, 'display', 'block');
      var box = geometry.position(target, true);
      if (target === baseWindow.body() || target === baseWindow.doc) {
        // Target is the whole doc, so scale to viewport.
        box = winUtils.getBox();
        box.x = box.l;
        box.y = box.t;
      }

      var cntrIndicator = geometry.getMarginBox(this._centerNode);
      domStyle.set(this._centerNode, 'display', curStyle);

      // Figure out how to zIndex this thing over the target.
      var zi = domStyle.get(target, 'zIndex');
      var ziUl = zi;
      var ziIn = zi;

      if (this.zIndex === 'auto') {
        if (zi != 'auto') {
          // honor user-defined z-index
          ziUl = parseInt(ziUl, 10);
          ziIn = parseInt(ziIn, 10);
        } else {
          // We need to search up the chain to see if there are any parent zIndexs to overlay.
          var cNode = target;
          if (cNode && cNode !== baseWindow.body() && cNode !== baseWindow.doc) {
            cNode = target.parentNode;
            var oldZi = -100000;
            while (cNode && cNode !== baseWindow.body()) {
              zi = domStyle.get(cNode, 'zIndex');
              if (!zi || zi === 'auto') {
                cNode = cNode.parentNode;
              } else {
                var newZi = parseInt(zi, 10);
                if (oldZi < newZi) {
                  oldZi = newZi;
                  ziUl = newZi + 1;
                  ziIn = newZi + 1;
                }
                // Keep looking until we run out, we want the highest zIndex.
                cNode = cNode.parentNode;
              }
            }
          }
        }
      } else {
        // honor user-defined z-index
        ziUl = parseInt(this.zIndex, 10);
        ziIn = parseInt(this.zIndex, 10);
      }

      domStyle.set(this._centerNode, 'zIndex', ziIn);
      domStyle.set(this._underlayNode, 'zIndex', ziUl);

      var pn = target.parentNode;
      if (pn && pn !== baseWindow.body() &&
        target !== baseWindow.body() &&
        target !== baseWindow.doc) {

        // If the parent is the body tag itself, we can avoid all this — the body
        // takes care of overflow. Besides, browser weirdness with height/width on
        // body causes problems with this sort of intersect testing anyway.
        var obh = box.h;
        var obw = box.w;
        var pnBox = geometry.position(pn, true);

        // Shift the parent width/height a bit if scrollers are present.
        pnBox.w -= pn.scrollHeight > pn.clientHeight &&
          pn.clientHeight > 0 ? scrollers.v : 0;
        pnBox.h -= pn.scrollWidth > pn.clientWidth &&
          pn.clientWidth > 0 ? scrollers.h : 0;

        // Figure out if we need to adjust the overlay to fit a viewable area, then
        // resize it (we saved the original height/width above).
        if (pnBox.w < box.w) {
          box.w = box.w - pnBox.w;
        }
        if (pnBox.h < box.h) {
          box.h = box.h - pnBox.h;
        }

        // Look at the y positions and see if we intersect with the viewport borders.
        var vpTop = pnBox.y;
        var vpBottom = pnBox.y + pnBox.h;
        var bTop = box.y;
        var bBottom = box.y + obh;
        var vpLeft = pnBox.x;
        var vpRight = pnBox.x + pnBox.w;
        var bLeft = box.x;
        var bRight = box.x + obw;
        var delta;
        // Adjust the height now
        if (bBottom > vpTop && bTop < vpTop) {
          box.y = pnBox.y;
          // intersecting top, need to do some shifting.
          delta = vpTop - bTop;
          var visHeight = obh - delta;
          // If the visible height < viewport height, we need to shift it.
          if (visHeight < pnBox.h) {
            box.h = visHeight;
          } else {
            // Deal with horizontal scrollbars if necessary.
            box.h -= 2 * (pn.scrollWidth > pn.clientWidth &&
              pn.clientWidth > 0 ? scrollers.h : 0);
          }
        } else if (bTop < vpBottom && bBottom > vpBottom) {
          // Intersecting bottom, just figure out how much overlay to show.
          box.h = vpBottom - bTop;
        } else if (bBottom <= vpTop || bTop >= vpBottom) {
          // Outside view, hide it.
          box.h = 0;
        }

        // adjust width
        if (bRight > vpLeft && bLeft < vpLeft) {
          box.x = pnBox.x;
          // intersecting left, need to do some shifting.
          delta = vpLeft - bLeft;
          var visWidth = obw - delta;
          // If the visible width < viewport width, we need to shift it.
          if (visWidth < pnBox.w) {
            box.w = visWidth;
          } else {
            // Deal with horizontal scrollbars if necessary.
            box.w -= 2 * (pn.scrollHeight > pn.clientHeight &&
              pn.clientHeight > 0 ? scrollers.w : 0);
          }
        } else if (bLeft < vpRight && bRight > vpRight) {
          // Intersecting right, just figure out how much overlay to show.
          box.w = vpRight - bLeft;
        } else if (bRight <= vpLeft || bLeft >= vpRight) {
          // Outside view, hide it.
          box.w = 0;
        }
      }

      if (box.h > 0 && box.w > 0) {
        // Set position and size of the blocking div overlay.
        domStyle.set(this._underlayNode, {
          display: 'block',
          width: box.w + 'px',
          height: box.h + 'px',
          top: box.y + 'px',
          left: box.x + 'px'
        });

        var styles = ['borderRadius', 'borderTopLeftRadius',
          'borderTopRightRadius', 'borderBottomLeftRadius',
          'borderBottomRightRadius'];
        this._cloneStyles(styles);
        // Browser-prefixed border-radius (evergreen browsers ignore the unknown ones).
        styles = ['MozBorderRadius', 'MozBorderRadiusTopleft',
          'MozBorderRadiusTopright', 'MozBorderRadiusBottomleft',
          'MozBorderRadiusBottomright', 'WebkitBorderRadius',
          'WebkitBorderTopLeftRadius', 'WebkitBorderTopRightRadius',
          'WebkitBorderBottomLeftRadius', 'WebkitBorderBottomRightRadius'
        ];
        this._cloneStyles(styles);

        var cntrIndicatorTop = (box.h / 2) - (cntrIndicator.h / 2);
        var cntrIndicatorLeft = (box.w / 2) - (cntrIndicator.w / 2);
        // Only show the image if there is height and width room.
        if (box.h >= cntrIndicator.h && box.w >= cntrIndicator.w) {
          domStyle.set(this._centerNode, {
            top: (cntrIndicatorTop + box.y) + 'px',
            left: (cntrIndicatorLeft + box.x) + 'px',
            display: 'block'
          });
        } else {
          domStyle.set(this._centerNode, 'display', 'none');
        }
      } else {
        // Target has no size, display nothing on it!
        domStyle.set(this._underlayNode, 'display', 'none');
        domStyle.set(this._centerNode, 'display', 'none');
      }
      if (this._resizeCheck === null) {
        // Set an interval timer that checks the target size and scales as needed.
        // Checking every 10th of a second seems to generate a fairly smooth update.
        var self = this;
        this._resizeCheck = setInterval(function () { self._size(); }, 100);
      }
    }
  },

  _cloneStyles: function (list) {
    // summary:
    //		Internal function to clone a set of styles from the target to the underlay.
    // tags:
    //		private
    array.forEach(list, function (s) {
      domStyle.set(this._underlayNode, s, domStyle.get(this.target, s));
    }, this);
  },

  _fadeIn: function () {
    // summary:
    //		Internal function that does the opacity style fade in animation.
    // tags:
    //		private
    var self = this;
    var underlayNodeAnim = baseFx.animateProperty({
      duration: self.duration,
      node: self._underlayNode,
      properties: { opacity: { start: 0, end: self.opacity } }
    });
    var imageAnim = baseFx.animateProperty({
      duration: self.duration,
      node: self._centerNode,
      properties: { opacity: { start: 0, end: 1 } },
      onEnd: function () {
        self.onShow();
        delete self._anim;
      }
    });
    this._anim = fx.combine([underlayNodeAnim, imageAnim]);
    this._anim.play();
  },

  _fadeOut: function () {
    // summary:
    //		Internal function that does the opacity style fade out animation.
    // tags:
    //		private
    var self = this;
    var underlayNodeAnim = baseFx.animateProperty({
      duration: self.duration,
      node: self._underlayNode,
      properties: { opacity: { start: self.opacity, end: 0 } },
      onEnd: function () {
        domStyle.set(this.node, { 'display': 'none', 'zIndex': '-1000' });
      }
    });
    var imageAnim = baseFx.animateProperty({
      duration: self.duration,
      node: self._centerNode,
      properties: { opacity: { start: 1, end: 0 } },
      onEnd: function () {
        domStyle.set(this.node, { 'display': 'none', 'zIndex': '-1000' });
        self.onHide();
        self._enableOverflow();
        delete self._anim;
      }
    });
    this._anim = fx.combine([underlayNodeAnim, imageAnim]);
    this._anim.play();
  },

  _ignore: function (e) {
    // summary:
    //		Function to ignore events that occur on the overlay.
    // tags:
    //		private
    if (e) {
      event.stop(e);
    }
  },

  _scrollerWidths: function () {
    // summary:
    //		Calculate the size of the vertical and horizontal scrollbars.
    // returns:
    //		Object of form {v: Number, h: Number}.
    // tags:
    //		private
    var div = construct.create('div');
    domStyle.set(div, {
      position: 'absolute',
      opacity: 0,
      overflow: 'hidden',
      width: '50px',
      height: '50px',
      zIndex: '-100',
      top: '-200px',
      padding: '0px',
      margin: '0px'
    });
    var iDiv = construct.create('div');
    domStyle.set(iDiv, {
      width: '200px',
      height: '10px'
    });
    div.appendChild(iDiv);
    baseWindow.body().appendChild(div);

    // Figure out content size before and after scrollbars are there, then subtract.
    var b = geometry.getContentBox(div);
    domStyle.set(div, 'overflow', 'scroll');
    var a = geometry.getContentBox(div);
    baseWindow.body().removeChild(div);
    return { v: b.w - a.w, h: b.h - a.h };
  },

  /* The following are functions that tie into _Widget.attr() */

  _setTextAttr: function (text) {
    // summary:
    //		Allow widget.attr to set the text displayed in center (text display).
    this._textNode.innerHTML = text;
    this.text = text;
  },

  _setColorAttr: function (c) {
    // summary:
    //		Allow widget.attr to set the color used for the translucent overlay.
    domStyle.set(this._underlayNode, 'backgroundColor', c);
    this.color = c;
  },

  _setImageTextAttr: function (text) {
    // summary:
    //		Allow widget.attr to set the ALT text for the image (image center display).
    attr.set(this._imageNode, 'alt', text);
    this.imageText = text;
  },

  _setImageAttr: function (url) {
    // summary:
    //		Allow widget.attr to set the url source for the center image.
    attr.set(this._imageNode, 'src', url);
    this.image = url;
  },

  _setCenterIndicatorAttr: function (indicator) {
    // summary:
    //		Allow widget.attr to set the center indicator, either image or text.
    this.centerIndicator = indicator;
    if (indicator === 'image') {
      this._centerNode = this._imageNode;
      domStyle.set(this._textNode, 'display', 'none');
    } else {
      this._centerNode = this._textNode;
      domStyle.set(this._imageNode, 'display', 'none');
    }
  },

  _setTargetAttr: function (target) {
    // summary:
    //		Allow widget.attr to set the target used.
    if (typeof target === 'string') {
      var w = registry.byId(target);
      this._set('target', w ? w.domNode : dom.byId(target));
    } else {
      this._set('target', target);
    }
  },

  _disableOverflow: function () {
    // summary:
    //		Disable scrollbars on the body. Only used if the overlay targets the body
    //		or the document.
    if (this.target === baseWindow.body() || this.target === baseWindow.doc) {
      // Store the overflow state we have to restore later.
      this._overflowDisabled = true;
      var body = baseWindow.body();
      if (body.style && body.style.overflow) {
        this._oldOverflow = domStyle.get(body, 'overflow');
      } else {
        this._oldOverflow = '';
      }
      domStyle.set(body, 'overflow', 'hidden');
    }
  },

  _enableOverflow: function () {
    // summary:
    //		Restore scrollbars on the body. Only used if the overlay targets the body
    //		or the document.
    if (this._overflowDisabled) {
      delete this._overflowDisabled;
      var body = baseWindow.body();
      // Restore all the overflow.
      domStyle.set(body, 'overflow', this._oldOverflow);
      delete this._oldOverflow;
    }
  }
});
